import type { HtmlAttributes } from '@studio/design-system/props';
import { toUri } from '@studio/models/video';
import { isNotNil } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import { Content } from './content.jsx';
import { Footer } from './footer.jsx';
import { Panel as _Panel } from '#~/common/editor/index.js';
import { locateDevice } from '#~/room/utils.js';
import type { Device } from '@studio/models/device';
import { StatusStreamState } from '@tryiris-ai/common.protojs/agent/v1/agent';

const isRtspReadyOverrideEnabled = (): boolean => {
  try {
    const runtime = globalThis as {
      __irisForceRtspVideoReady?: boolean;
      window?: { electron?: { process?: { env?: Record<string, string> } } };
    };
    const e2eEnabled = runtime.window?.electron?.process?.env?.IRIS_E2E_TEST === '1';
    return e2eEnabled && runtime.__irisForceRtspVideoReady === true;
  } catch {
    return false;
  }
};

/** Video `<Panel>` component. */
export const Panel = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  // As soon as the candidate has something that resembles a video URI, we can continue.
  const isReady = useStore((µ) => {
    if (isRtspReadyOverrideEnabled()) {
      return true;
    }

    const config = µ.candidate.video;
    const ready = config ? isNotNil(toUri(config)) : false;

    const videoPreviewDeviceID = µ.previews.video?.deviceId;
    const device = locateDevice(videoPreviewDeviceID ?? ('' as Device['id'])) ?? undefined;

    const safeReady = ready && !!device;

    const playing = safeReady && device.video?.status === StatusStreamState.STREAM_STATE_PLAYING;

    // Fold the empty-creds probe signal in. While the probe is in flight the live
    // transient may be the credential-FREE empty probe, which can reach PLAYING on
    // a candidate.video that does not yet carry the real creds — enabling Continue
    // would commit a device with no credentials. This Panel stays the sole writer
    // that raises video.isReady to true (the reset in resetVideoConfiguration only
    // clears it to false); reading `probing` here means the flag flipping false at
    // DONE recomputes this value and re-raises isReady (an isReady overwrite from
    // rtsp.tsx could not, since this effect only re-writes when its own computed
    // value changes).
    return playing && !µ.video.probing;
  });

  useEffect(() => {
    producer(({ video }) => {
      video.isReady = isReady;
    });
  }, [isReady]);

  return (
    <_Panel {...props}>
      <Content />
      <Footer />
    </_Panel>
  );
};
